import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import PatientSelectScroll from "../../../../../components/PatientSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import CustomTimePicker from "../../../../../components/CustomTimePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
// import Input from "../../../../../components/Input";
import MultiSelect from "../../../../../components/MultiSelect";
import Text from "../../../../../components/Text";
import StyledFormModal from "../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddTask } from "../../../../../queries/collaboration/tasks/use-add-task";
import { useTaskOptionsList } from "../../../../../queries/collaboration/tasks/use-task-title-list";
import { useTaskTypeList } from "../../../../../queries/collaboration/tasks/use-task-type-list";
import { useUpdateTask } from "../../../../../queries/collaboration/tasks/use-update-task";
import ProviderSelectScroll from "../../../../../components/ProviderSelectScroll";

import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { MEDIUM_LIST } from "../../patient/patient-list/patient-chart-new/assessment/PatientAssementForms/assessment-constant";
import {
  TASK_INITIAL_VALIDATION_SCHEMA,
  TASK_INITIAL_VALUES,
  TASK_PRIORITIES,
  TaskFormik,
} from "../collaboration-task-constant";
import AddTaskTitleModal from "./AddTaskTitleModal";
// import { TaskRecurringConfig } from "./TaskRecurringConfig";
// import { useTaskCancel } from "../../../../../queries/collaboration/tasks/use-task-cancel";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import NumberInput from "../../../../../components/NumberInput";
import { INPUT_FIELD_HEIGHT } from "../../../../../theme";

interface UpsertTaskDrawerProps {
  disclosureHost: DisclosureHost;
  taskType: string;
  patientData: any;
  isProviderCentric?: boolean;
}

const UpsertTaskDrawer = ({
  disclosureHost,
  // todo: need to remove batch task as per req
  taskType: _,
  patientData,
  isProviderCentric = true,
}: UpsertTaskDrawerProps) => {
  const showSnackbar = useSnackbar();

  // define here prefill date for due date and time should continue to add 1 hour from current time
  const addTaskTitleDisclosureHost = useDisclosure();
  const appointmentId = useQueryParams<string>("appointmentid");
  const [taskListSearch, setTaskListSearch] = useDebounce<string>("");
  const [taskTypeSearch, setTaskTypeSearch] = useDebounce<string>("");
  const [taskListPagination, setTaskListPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [taskTypePagination, setTaskTypePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [batchSelectedTask, setBatchSelectedTask] = useState<TaskFormik[]>([]);
  const mergeDateTime = (date?: Date, time?: Date) => {
    if (!date || !time) return;

    const preDate = new Date(date);
    preDate?.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
    return preDate?.toISOString();
  };

  const [currentUser] = useCurrentUser();
  const currentUserOption = currentUser
    ? {
        value: currentUser.uuid,
        label: `${currentUser.firstName} ${currentUser.lastName}`,
      }
    : null;

  const INITIAL_VALUES = {
    ...TASK_INITIAL_VALUES,
    providerTask: isProviderCentric,
    patient: patientData,
    dueDate: prefillDate,
    assignTo: currentUserOption,
  };

  const formik = useFormik<TaskFormik>({
    initialValues: disclosureHost.data
      ? {
          patient: patientData,
          ...disclosureHost.data,
          // Edit is a short flow — the drawer never renders Recurring or Set
          // Reminder, so both stay off. Left on, the shared schema keeps
          // validating inputs the edit form has no field for and Save fails
          // silently. These also carry the false straight into the payload.
          isRecurring: false,
          reminder_set: false,
          endDate: undefined,
        }
      : INITIAL_VALUES,
    validationSchema: TASK_INITIAL_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // const { mutateAsync: cancelTask } = useTaskCancel();
  // const cancelTaskHandler = (taskUuid: string) => {
  //   showToastAndResetForm(
  //     cancelTask({
  //       bulkCancel: {
  //         taskId: [taskUuid],
  //       },
  //       xTENANTID: getXtenantId() ?? undefined,
  //     })
  //   );
  // };

  const queryClient = useQueryClient();
  const { mutateAsync: createTask } = useAddTask();
  const { data: taskTypeActionList, isFetching: isTaskTypeFetching } =
    useTaskTypeList({
      gridSorting: [],
      pagination: taskTypePagination,
      searchString: taskTypeSearch,
      xTENANTID: getXtenantId(),
      enabled: disclosureHost.opened,
    });
  const { data: taskOptionsList, isFetching: isTaskOptionsFetching } =
    useTaskOptionsList({
      pagination: taskListPagination,
      gridSorting: [],
      status: true,
      archive: false,
      searchString: taskListSearch,
      xTENANTID: getXtenantId(),
    });

  const taskListOptions = useMemo(() => {
    return (
      taskOptionsList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })) ?? []
    );
  }, [taskOptionsList?.data?.content]);

  const { mutateAsync: updateTask } = useUpdateTask();

  const taskTypeActionOptions = useMemo(
    () =>
      taskTypeActionList?.data?.content.map((it) => ({
        value: it?.uuid,
        label: it?.title,
      })) ?? [],
    [taskTypeActionList?.data?.content]
  );

  const getObj = (obj: { value: string; label: string }) =>
    obj ? { [obj.value]: obj.label } : undefined;

  async function handleSubmit(values?: any) {
    const selectedPatient = getObj(values.patient);
    const actions = getObj(values.actions);
    const assignTo = getObj(values.assignTo);

    // const payload = {
    //   patientAssessments: {
    //     ...v,
    //     reminders: v?.reminder_set ? v?.reminders : undefined,
    //     assessment: v.selectedForms?.map((v) => v.value),
    //     scheduleTime: mergeDateTime(v.recordedDate, v.recordedTime),
    //     reminderMedium: ["EMAIL"],
    //     recordedTime: undefined,
    //   },

    const payload = {
      task: {
        ...values,
        title: values.title?.label,
        patient: selectedPatient,
        actions: actions,
        assignTo: assignTo,
        // Set Reminder exists only while adding a task. On edit the drawer hides
        // it, so send back the schedule the task already carries (it rides along
        // in `values`) instead of rebuilding it from fields the edit form never
        // collected. Recurring is no longer collected in either flow.
        ...(disclosureHost.data
          ? {
              isRecurring: false,
              reminder_set: false,
            }
          : {
              isRecurring: false,
              endDate: undefined,
              // Recurring is hidden on the task form, so no schedule is sent.
              // endDate: values.endDate
              //   ? values.endDate.toISOString()
              //   : undefined,
              // recurringType: values?.recurringFrequency ?? undefined,
              reminders: values.reminder_set
                ? {
                    reminderMedium: values.reminder_mediums,
                    reminderBeforeNumber: values.reminder_before_number,
                    reminderBeforeUnit: values.reminder_before_unit,
                  }
                : undefined,
              reminderMedium: values.reminder_set
                ? values.reminder_mediums
                : undefined,
            }),
        recurringFrequency: undefined,
        reminder_set: undefined,
        reminder_mediums: undefined,
        reminder_before_number: undefined,
        reminder_before_unit: undefined,
        scheduleTime: values?.isScheduled
          ? mergeDateTime(values.recordedDate, values.recordedTime)
          : undefined,
        appointmentId: appointmentId ?? undefined,
      },
      xTENANTID: getXtenantId(),
    };

    setBatchSelectedTask((prev) => [
      ...prev,
      { ...payload.task, id: (batchSelectedTask?.length ?? 0) + 1 },
    ]);

    if (disclosureHost.data) showToastAndResetForm(updateTask(payload));
    else showToastAndResetForm(createTask(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();

    // if (taskType !== "batch_task") {
    disclosureHost.close();
    // }
  }

  const handleDateValue = (date: Date, type: "date" | "time") => {
    if (type === "time" && formik.values?.dueDate) {
      const preDate = new Date(formik.values.dueDate);
      preDate?.setHours(date.getHours(), date.getMinutes(), date.getSeconds());

      formik.setFieldValue("dueDate", preDate?.toISOString());
    } else {
      if (
        formik.values.isRecurring &&
        dayjs(formik.values?.endDate).isBefore(dayjs(date.toISOString()))
      ) {
        showSnackbar({
          message: "Due date cannot be after the end date of the task",
          type: "error",
        });
        return;
        // const newDate = new Date(date);
        // newDate.setDate(newDate.getDate() + 1);
        // formik.setFieldValue("endDate", newDate.toISOString());
      }

      formik.setFieldValue("dueDate", date?.toISOString());
    }
  };

  // const TitleMapper: Record<string, string> = {
  //   single_task: "Task",
  //   batch_task: "Batch Task",
  // };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle={` ${disclosureHost?.data ? "Edit" : "Add"} ${isProviderCentric ? "Provider" : "Patient"} Task`}
      dialogAction={
        <>
          {disclosureHost.data && (
            <StyledButtonV2
              label="Cancel"
              variant="outlined"
              onClick={() => disclosureHost.close()}
              sx={{ width: "105px" }}
            />
          )}
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={formik.submitForm}
            sx={{ width: "105px" }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "800px",
          height: "56vh",
        },
      }}
    >
      <Flex gap={1.5} flexDirection={"column"} pt={2}>
        <AddTaskTitleModal disclosureHost={addTaskTitleDisclosureHost} />
        <Grid container spacing={2}>
          {/* <Grid
            item
            sm={12}
            container
            rowSpacing={1}
            lg={12}
            alignItems={"start"}
          >
            {!patientData && (
              <FormControlLabel
                disabled={isProviderCentric && disclosureHost.data}
                name="providerTask"
                control={<Checkbox />}
                label={<Text fontWeight={500}>Assign task to Provider</Text>}
                checked={formik.values.providerTask}
                onChange={formik.handleChange}
              />
            )}
          </Grid> */}
          <Grid item xs={6} container rowSpacing={1}>
            <Flex gap={1} width="100%" height={"100%"} mt={1}>
              <Box width={"82%"}>
                <AsyncSelectScroll
                  required
                  label="Title"
                  placeholder="Select Title"
                  onSearch={(val) => {
                    setTaskListSearch(val);
                    setTaskListPagination((p) => ({ ...p, page: 0 }));
                  }}
                  options={taskListOptions}
                  value={formik.values.title}
                  onChange={(opt) => formik.setFieldValue("title", opt)}
                  error={(formik.touched?.title && formik.errors?.title) as any}
                  pagination={taskListPagination}
                  setPagination={setTaskListPagination}
                  totalElements={taskOptionsList?.data?.page?.totalElements}
                  loading={isTaskOptionsFetching}
                />
              </Box>
              <Box width={"14%"}>
                <Button
                  sx={{ height: INPUT_FIELD_HEIGHT }}
                  variant="outlined"
                  onClick={() => {
                    addTaskTitleDisclosureHost.open();
                    // console.log(
                    //   "val here check disc click",
                    //   addTaskTitleDisclosureHost
                    // );
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Flex>
            {/* <Input
              required
              label={"Title"}
              textFieldProps={{
                placeholder: "Enter Title",
                name: "title",
                value: formik.values.title,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={(formik.touched?.title && formik.errors?.title) as any}
            /> */}
          </Grid>
          <Grid item xs={6}>
            <CustomSelect
              required
              name="priority"
              handleBlur={formik.handleBlur}
              value={formik.values.priority}
              onChange={(val) => formik.setFieldValue("priority", val)}
              label="Priority"
              options={TASK_PRIORITIES}
              placeholder="Select"
              error={(formik.touched.priority && formik.errors.priority) as any}
            />
          </Grid>

          {/* {!formik.values.providerTask && ( */}
          <Grid item xs={6}>
            <PatientSelectScroll
              handleBlur={formik.handleBlur}
              placeholder="Search Patient"
              label="Patient Name"
              required={!isProviderCentric}
              disabled={!!patientData}
              name="patient"
              value={formik.values.patient ?? null}
              onChange={(value) => {
                formik.setFieldValue("patient", value);
                if (!value) {
                  formik.setFieldValue("actions", null);
                }
              }}
              error={(formik.touched?.patient && formik.errors?.patient) as any}
            />
          </Grid>
          {/* )} */}
          {/* ECRH-4513: Action field is disabled until a patient is selected */}
          <Grid item xs={6}>
            <AsyncSelectScroll
              required={!!formik.values.patient}
              disabled={!formik.values.patient}
              name="actions"
              handleBlur={formik.handleBlur}
              value={formik.values.actions}
              onChange={(val) => formik.setFieldValue("actions", val)}
              label="Action"
              options={taskTypeActionOptions}
              placeholder="Select"
              error={(formik.touched.actions && formik.errors.actions) as any}
              onSearch={(val) => {
                setTaskTypeSearch(val);
                setTaskTypePagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={taskTypePagination}
              setPagination={setTaskTypePagination}
              totalElements={taskTypeActionList?.data?.page?.totalElements}
              loading={isTaskTypeFetching}
            />
          </Grid>

          <Grid item xs={6}>
            <ErrorHelper
              error={formik.touched?.dueDate && (formik.errors.dueDate as any)}
            >
              <Flex gap={1.5} width={"100%"}>
                <Box width={"50%"}>
                  <CustomDatePicker
                    disableError
                    error={
                      formik.touched?.dueDate && (formik.errors.dueDate as any)
                    }
                    required
                    name="dueDate"
                    handleBlur={formik.handleBlur}
                    placeholder="Choose Date"
                    value={formik.values.dueDate}
                    minDate={dayjs(new Date())}
                    label="Due Date"
                    onChange={(val) =>
                      handleDateValue(dayjs(val)?.toDate(), "date")
                    }
                  />
                </Box>
                <Box width={"50%"}>
                  <CustomTimePicker
                    disableError
                    error={
                      formik.touched?.dueDate && (formik.errors.dueDate as any)
                    }
                    required
                    label="Due Time"
                    name="dueTime"
                    handleBlur={formik.handleBlur}
                    value={formik.values.dueDate}
                    onChange={(value) =>
                      handleDateValue(dayjs(value)?.toDate(), "time")
                    }
                  />
                </Box>
              </Flex>
            </ErrorHelper>
          </Grid>
          <Grid item xs={6}>
            <ProviderSelectScroll
              required={formik.values.providerTask}
              name="assignTo"
              handleBlur={formik.handleBlur}
              disabled={!!disclosureHost?.data}
              label={isProviderCentric ? "Assignee" : "Task Reviewer"}
              value={formik.values.assignTo ?? null}
              onChange={(value) => {
                formik.setFieldValue("assignTo", value);
              }}
              prefillListOption={formik.values.assignTo ?? undefined}
              error={
                (formik.touched?.assignTo && formik.errors?.assignTo) as any
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              minRows={3}
              label="Task Description"
              placeholder="Task Description"
              onChange={formik.handleChange}
              name={"description"}
              value={formik.values.description}
            />
          </Grid>

          {/* todo: schedule task feature for task
          <Grid item sm={12} md={6} lg={12}>
            <FormControlLabel
              name="isScheduled"
              control={<Checkbox />}
              label={<Text fontWeight={500}>Schedule Task</Text>}
              checked={formik.values.isScheduled}
              onChange={(_, checked) => {
                formik.setFieldValue(
                  "isScheduled",
                  !formik.values.isScheduled
                );

                if (checked) {
                  formik.setFieldValue("recordedDate", undefined);
                  formik.setFieldTouched("recordedDate", false);
                  formik.setFieldValue("recordedTime", undefined);
                  formik.setFieldTouched("recordedDate", false);
                }
              }}
            />
          </Grid> */}
          {formik.values.isScheduled && (
            <Grid item xs={12}>
              <Flex gap={2}>
                <CustomDatePicker
                  required
                  label="Date"
                  name="recordedDate"
                  minDate={dayjs()}
                  handleBlur={formik.handleBlur}
                  value={formik.values?.recordedDate}
                  onChange={(val) =>
                    formik.setFieldValue("recordedDate", val?.toISOString())
                  }
                  error={
                    (formik.touched?.recordedDate &&
                      formik.errors?.recordedDate) as any
                  }
                />

                <CustomTimePicker
                  required
                  label="Time"
                  minTime={
                    formik.values?.recordedDate &&
                    dayjs(formik.values?.recordedDate).isSame(dayjs(), "day")
                      ? dayjs()
                      : undefined
                  }
                  name="recordedTime"
                  handleBlur={formik.handleBlur}
                  value={formik.values?.recordedTime}
                  onChange={(val) => {
                    formik.setFieldValue("recordedTime", dayjs(val)?.toDate());
                  }}
                  error={
                    (formik.touched?.recordedTime &&
                      formik.errors?.recordedTime) as any
                  }
                />
              </Flex>
            </Grid>
          )}

          {formik.values.isScheduled && (
            <Grid item xs={12}>
              <CustomSelect
                readOnly={true}
                name="reminderMedium"
                label="Medium"
                value={"EMAIL"}
                options={MEDIUM_LIST.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                onChange={(val) => formik.setFieldValue("reminderMedium", val)}
                handleBlur={formik.handleBlur}
                error={
                  formik?.touched?.reminderMedium &&
                  (formik.errors?.reminderMedium as any)
                }
              />
            </Grid>
          )}
          {!disclosureHost.data && (
            <>
              {/* Recurring is hidden on Tasks until product confirms the field is retired; existing recurring tasks keep their schedule.
              <Grid
                item
                sm={12}
                md={6}
                container
                rowSpacing={1}
                lg={6}
                alignItems={"start"}
              >
                <FormControlLabel
                  name="isRecurring"
                  control={<Checkbox />}
                  label={<Text fontWeight={500}> Recurring</Text>}
                  checked={formik.values.isRecurring}
                  onChange={() => {
                    formik.setFieldValue(
                      "isRecurring",
                      !formik.values.isRecurring
                    );
                    if (!formik.values.isRecurring) {
                      formik.setFieldValue("recurringFrequency", "DAILY");
                    } else {
                      formik.setFieldValue("recurringFrequency", undefined);
                    }
                  }}
                />

                {formik.values.isRecurring && (
                  <TaskRecurringConfig formik={formik as any} />
                )}
              </Grid>
              */}

              <Grid item sm={12} md={6} lg={6}>
                <FormControlLabel
                  name="reminder_set"
                  control={<Checkbox />}
                  label={<Text fontWeight={500}>Set Reminder</Text>}
                  checked={formik.values.reminder_set}
                  onChange={() => {
                    formik.setFieldValue(
                      "reminder_set",
                      !formik.values.reminder_set
                    );
                  }}
                />

                {formik.values.reminder_set && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item sm={12}>
                        <MultiSelect
                          required
                          label="Reminder Mediums"
                          name="reminder_mediums"
                          handleBlur={formik.handleBlur}
                          options={[
                            { value: "EMAIL", label: "E-Mail" },
                            { value: "SMS", label: "SMS" },
                            {
                              value: "PUSH_NOTIFICATION",
                              label: "Push Notification",
                            },
                          ]}
                          value={formik.values.reminder_mediums}
                          onChange={(values) => {
                            formik.setFieldValue("reminder_mediums", values);
                          }}
                          error={
                            formik.touched.reminder_mediums &&
                            (formik.errors.reminder_mediums as any)
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <NumberInput
                          required
                          textFieldProps={{
                            name: "reminder_before_number",
                            inputProps: { min: 1 },
                            value: formik.values.reminder_before_number,
                            onChange: (e) => {
                              formik.setFieldValue(
                                "reminder_before_number",
                                e.currentTarget.value
                              );
                            },
                          }}
                          label="Remind Before"
                          error={
                            formik.touched.reminder_before_number &&
                            (formik.errors.reminder_before_number as any)
                          }
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <CustomSelect
                          required={true}
                          handleBlur={formik.handleBlur}
                          label=""
                          name="reminder_before_unit"
                          options={[
                            { value: "MINUTES", label: "Minutes" },
                            { value: "HOURS", label: "Hours" },
                            { value: "DAYS", label: "Days" },
                          ]}
                          clearable={false}
                          value={formik.values.reminder_before_unit}
                          onChange={(value) => {
                            formik.setFieldValue("reminder_before_unit", value);
                          }}
                          error={
                            formik.touched.reminder_before_unit &&
                            (formik.errors.reminder_before_unit as any)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
        {/* {taskType === "batch_task" && batchSelectedTask.length > 0 && (
          <BatchTaskTable rows={batchSelectedTask} />
        )} */}
      </Flex>
    </StyledFormModal>
  );
};

export default UpsertTaskDrawer;

const prefillDate = dayjs().add(1, "hour").toISOString();
